import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { InfoTip } from "./DonorMFCASummary";
import RecoveryDonut from "../ui/RecoveryDonut";
import "../../style/DonorInventory.css";
import "../../style/mfca-dashboard.css";

const getBaseline = (acqCost, refPrice, listingCost) => acqCost || refPrice || listingCost || 0;

const DonorOpportunityBreakdown = ({
  transactions = [],
  expiredListings = [],
  darkMode,
}) => {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState("unrealizedValue");
  const [sortDir, setSortDir] = useState("desc");

  const rows = useMemo(() => {
    const map = {};

    for (const txn of transactions) {
      const mat = txn.material || "Unknown";
      if (!map[mat]) map[mat] = { material: mat, category: txn.materialCategory || null, listed: 0, matched: 0, expired: 0, totalValue: 0, matchedValue: 0 };
      const baseline = getBaseline(txn.acquisitionCost, txn.referencePricePerLb, txn.listingCost);
      const qty = txn.quantity || 0;
      const accepted = txn.quantityAccepted != null ? txn.quantityAccepted : qty;

      map[mat].listed += qty;
      map[mat].matched += accepted;
      map[mat].totalValue += baseline * qty;
      map[mat].matchedValue += baseline * accepted;
    }

    for (const exp of expiredListings) {
      const mat = exp.material || "Unknown";
      if (!map[mat]) map[mat] = { material: mat, category: exp.materialCategory || null, listed: 0, matched: 0, expired: 0, totalValue: 0, matchedValue: 0 };
      const baseline = getBaseline(exp.acquisitionCost, exp.referencePricePerLb, exp.cost);
      const remaining = exp.remainingQty || 0;

      map[mat].expired += remaining;
      map[mat].listed += remaining;
      map[mat].totalValue += baseline * remaining;
    }

    return Object.values(map).map((r) => {
      const unrealizedValue = r.totalValue - r.matchedValue;
      const diversionRate = r.listed > 0 ? (r.matched / r.listed) * 100 : 0;
      const unrealizedPct = r.totalValue > 0 ? (unrealizedValue / r.totalValue) * 100 : 0;
      return { ...r, unrealizedValue, diversionRate, unrealizedPct };
    });
  }, [transactions, expiredListings]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [rows, sortKey, sortDir]);

  const totals = useMemo(() => {
    const t = { listed: 0, matched: 0, totalValue: 0, matchedValue: 0, unrealizedValue: 0 };
    for (const r of rows) {
      t.listed += r.listed;
      t.matched += r.matched;
      t.totalValue += r.totalValue;
      t.matchedValue += r.matchedValue;
      t.unrealizedValue += r.unrealizedValue;
    }
    t.diversionRate = t.listed > 0 ? (t.matched / t.listed) * 100 : 0;
    t.unrealizedPct = t.totalValue > 0 ? (t.unrealizedValue / t.totalValue) * 100 : 0;
    return t;
  }, [rows]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const getSortIcon = (key) => {
    if (sortKey !== key) return faSort;
    return sortDir === "asc" ? faSortUp : faSortDown;
  };

  if (rows.length === 0) return null;

  const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtLbs = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 1 });

  const getInsight = (row) => {
    if (row.diversionRate >= 90) return null;
    if (row.expired > 0 && row.diversionRate < 70)
      return t("Consider extending deadline or pricing lower to move faster");
    if (row.diversionRate < 80)
      return t("Try different receivers — some may have higher tolerance for this material");
    return null;
  };

  return (
    <div className="chart-card chart-card--full">
      <div className="chart-card-header">
        <div className="chart-card-icon green">
          <FontAwesomeIcon icon={faTable} />
        </div>
        <h5 className="chart-card-title">
          {t("Material Diversion")}
          <InfoTip text={t("How your listed materials performed — how much found a receiver vs. what expired or was unmatched. Unrealized value is not a loss — it's value you could still capture next time.")} />
        </h5>
      </div>

      <div className="donor-materials-table-wrapper">
        <table className="donor-materials-table">
          <thead>
            <tr>
              <th>{t("Material")}</th>
              <th className="number-col">
                <button
                  type="button"
                  className="mfca-sort-btn"
                  onClick={() => handleSort("matched")}
                  aria-label={t("Sort by {{col}}", { col: t("Matched / Listed") })}
                >
                  {t("Matched / Listed")}
                  <FontAwesomeIcon icon={getSortIcon("matched")} className="mfca-sort-icon" />
                </button>
              </th>
              <th style={{ textAlign: "center" }}>
                <button
                  type="button"
                  className="mfca-sort-btn"
                  onClick={() => handleSort("diversionRate")}
                  aria-label={t("Sort by {{col}}", { col: t("Diversion") })}
                >
                  {t("Diversion")}
                  <InfoTip text={t("Green = matched and diverted from landfill. Amber = expired or unmatched.")} placement="bottom" />
                  <FontAwesomeIcon icon={getSortIcon("diversionRate")} className="mfca-sort-icon" />
                </button>
              </th>
              <th className="number-col">
                <button
                  type="button"
                  className="mfca-sort-btn"
                  onClick={() => handleSort("unrealizedValue")}
                  aria-label={t("Sort by {{col}}", { col: t("Unrealized Value") })}
                >
                  {t("Unrealized Value")}
                  <InfoTip text={t("Dollar amount and percentage of this material's total surplus value that was not matched to a receiver.")} placement="bottom" />
                  <FontAwesomeIcon icon={getSortIcon("unrealizedValue")} className="mfca-sort-icon" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const insight = getInsight(row);
              return (
                <tr key={row.material}>
                  <td className="material-name-cell">
                    <div className="material-name-content">
                      <span>{row.material}</span>
                      {row.category && (
                        <span className="material-category-badge">{t(row.category)}</span>
                      )}
                    </div>
                    {insight && (
                      <div className="chart-tooltip-subtitle" style={{ paddingLeft: 0, margin: "4px 0 0" }}>{insight}</div>
                    )}
                  </td>
                  <td className="number-col">
                    <strong>{fmtLbs(row.matched)}</strong>
                    <span className="recovery-of-total"> / {fmtLbs(row.listed)} lbs</span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <RecoveryDonut rate={row.diversionRate} />
                  </td>
                  <td className="number-col">
                    ${fmt(row.unrealizedValue)}
                    <span className="recovery-of-total"> ({row.unrealizedPct.toFixed(0)}%)</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr>
                <td><strong>{t("Total")}</strong></td>
                <td className="number-col">
                  <strong>{fmtLbs(totals.matched)}</strong>
                  <span className="recovery-of-total"> / {fmtLbs(totals.listed)} lbs</span>
                </td>
                <td style={{ textAlign: "center" }}>
                  <RecoveryDonut rate={totals.diversionRate} />
                </td>
                <td className="number-col">
                  <strong>${fmt(totals.unrealizedValue)}</strong>
                  <span className="recovery-of-total"> ({totals.unrealizedPct.toFixed(0)}%)</span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default DonorOpportunityBreakdown;
